import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { signIn, signUp } from '../auth'
import { colors, fontFamily } from '../tokens'

type Props = {
  onAuthed: () => void
  onCancel: () => void
}

export const AuthScreen = ({ onAuthed, onCancel }: Props) => {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const submit = async () => {
    setPending(true)
    setError(null)
    try {
      const result =
        mode === 'signin'
          ? await signIn.email({ email: email.trim(), password })
          : await signUp.email({ email: email.trim(), password, name: name.trim() || email.trim() })
      if (result.error) {
        setError(result.error.message ?? 'Authentication failed')
        return
      }
      onAuthed()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setPending(false)
    }
  }

  const canSubmit =
    email.trim().length > 3 && password.length >= 8 && (mode === 'signin' || name.trim().length > 0)

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{mode === 'signin' ? 'Sign in' : 'Create account'}</Text>
      <Text style={styles.subtitle}>
        {mode === 'signin'
          ? 'Welcome back. Sign in to see your appeals and follow up on outcomes.'
          : 'Create an account to save your appeals and get reminders.'}
      </Text>

      {mode === 'signup' ? (
        <>
          <Text style={styles.label}>Your name</Text>
          <TextInput
            accessibilityLabel="Name"
            autoComplete="name"
            editable={!pending}
            onChangeText={setName}
            placeholder="Alex Smith"
            style={styles.input}
            textContentType="name"
            value={name}
          />
        </>
      ) : null}

      <Text style={styles.label}>Email</Text>
      <TextInput
        accessibilityLabel="Email"
        autoCapitalize="none"
        autoComplete="email"
        editable={!pending}
        keyboardType="email-address"
        onChangeText={setEmail}
        placeholder="you@example.com"
        style={styles.input}
        textContentType="emailAddress"
        value={email}
      />

      <Text style={styles.label}>Password</Text>
      <TextInput
        accessibilityLabel="Password"
        autoCapitalize="none"
        autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
        editable={!pending}
        onChangeText={setPassword}
        placeholder="At least 8 characters"
        secureTextEntry
        style={styles.input}
        textContentType={mode === 'signup' ? 'newPassword' : 'password'}
        value={password}
      />

      <Pressable
        accessibilityRole="button"
        disabled={!canSubmit || pending}
        onPress={submit}
        style={[styles.primaryButton, (!canSubmit || pending) && styles.disabled]}
      >
        <Text style={styles.primaryButtonText}>
          {pending ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account'}
        </Text>
      </Pressable>

      <Pressable
        accessibilityRole="button"
        onPress={() => setMode((m) => (m === 'signin' ? 'signup' : 'signin'))}
        style={styles.secondary}
      >
        <Text style={styles.secondaryText}>
          {mode === 'signin'
            ? "Don't have an account? Sign up"
            : 'Already have an account? Sign in'}
        </Text>
      </Pressable>

      <Pressable accessibilityRole="button" onPress={onCancel} style={styles.secondary}>
        <Text style={styles.secondaryText}>← Back to band check</Text>
      </Pressable>

      {error ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { width: '100%', maxWidth: 400, gap: 8 },
  title: { fontSize: 26, fontWeight: '700', color: colors.ink, fontFamily: fontFamily.body },
  subtitle: { fontSize: 14, color: colors.muted, marginBottom: 12, fontFamily: fontFamily.body },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.ink,
    marginTop: 8,
    fontFamily: fontFamily.body,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: colors.surface,
    fontFamily: fontFamily.body,
  },
  primaryButton: {
    marginTop: 16,
    backgroundColor: colors.ink,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  disabled: { opacity: 0.5 },
  secondary: { paddingVertical: 8, alignItems: 'center' },
  secondaryText: { color: colors.primary[700], fontSize: 14, fontFamily: fontFamily.body },
  error: { color: colors.error[700], marginTop: 8, fontSize: 13, fontFamily: fontFamily.body },
})
